import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, CheckCircle2, ArrowRight } from 'lucide-react';

const API = `${window.location.origin}/api`;

const EVENT_TYPES = [
  'Wedding', 'Corporate Event', 'Gala or Fundraiser', 'Private Celebration',
  'Milestone Birthday', 'Anniversary', 'Rehearsal Dinner', 'Other',
];

/**
 * Strategy Session request modal.
 * Props:
 *   open, onClose, packageKey, packageLabel
 * On submit → hits /api/consulting/strategy-session → shows confirmation with Pay Now button.
 */
export const StrategySessionModal = ({ open, onClose, packageKey, packageLabel }) => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', event_type: '', event_date: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.event_type) {
      toast.error('Please share your name, email, and event type');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/consulting/strategy-session`, {
        ...form,
        package_interest: packageKey,
        origin_url: window.location.origin,
      });
      setResult(res.data);
      toast.success('Invitation sent — check your inbox');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const closeAndReset = () => {
    setForm({ name: '', email: '', phone: '', event_type: '', event_date: '', message: '' });
    setResult(null);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={closeAndReset}
      data-testid="strategy-modal"
    >
      <div
        className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-8 pb-4 border-b border-white/10">
          <div>
            <p className="text-[#D4AF37] text-[10px] uppercase tracking-[4px] mb-2">Request</p>
            <h3 className="font-heading text-2xl text-white leading-tight">
              {result ? 'Your invitation is on its way' : 'Strategy Session'}
            </h3>
            {!result && (
              <p className="text-white/50 text-sm mt-2">Interested in: <span className="text-[#D4AF37]">{packageLabel}</span></p>
            )}
          </div>
          <button
            onClick={closeAndReset}
            className="text-white/40 hover:text-white transition-colors"
            data-testid="strategy-modal-close"
          >
            <X size={22} />
          </button>
        </div>

        {result ? (
          /* Confirmation state */
          <div className="p-8 text-center">
            <CheckCircle2 className="w-14 h-14 text-[#D4AF37] mx-auto mb-4" />
            <p className="text-white/80 leading-relaxed mb-6">
              We&rsquo;ve sent a personal invitation to <span className="text-[#D4AF37]">{form.email}</span> with what to expect from your Strategy Session and a private pay link.
            </p>
            <p className="text-white/50 text-sm mb-8">
              Prefer to secure your session right now? Complete payment below and we&rsquo;ll reach out to schedule within one business day.
            </p>
            <a
              href={result.checkout_url}
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-8 py-4 text-xs uppercase tracking-widest hover:bg-[#C9A432] transition-colors"
              data-testid="strategy-pay-now-btn"
            >
              Pay $300 &amp; Reserve Session <ArrowRight size={14} />
            </a>
            <button
              onClick={closeAndReset}
              className="block mx-auto mt-6 text-white/40 hover:text-white text-xs uppercase tracking-widest"
            >
              Close
            </button>
          </div>
        ) : (
          /* Form state */
          <form onSubmit={handleSubmit} className="p-8 space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-2">Name</label>
                <input
                  type="text" value={form.name} onChange={handleChange('name')} required
                  className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                  data-testid="strategy-name"
                />
              </div>
              <div>
                <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-2">Email</label>
                <input
                  type="email" value={form.email} onChange={handleChange('email')} required
                  className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                  data-testid="strategy-email"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-2">Phone (optional)</label>
                <input
                  type="tel" value={form.phone} onChange={handleChange('phone')}
                  className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                  data-testid="strategy-phone"
                />
              </div>
              <div>
                <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-2">Event Type</label>
                <select
                  value={form.event_type} onChange={handleChange('event_type')} required
                  className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                  data-testid="strategy-event-type"
                >
                  <option value="">— Select —</option>
                  {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-2">Event Date (optional)</label>
              <input
                type="date" value={form.event_date} onChange={handleChange('event_date')}
                className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                data-testid="strategy-event-date"
              />
            </div>

            <div>
              <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-2">Anything we should know? (optional)</label>
              <textarea
                value={form.message} onChange={handleChange('message')} rows={3}
                className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                data-testid="strategy-message"
              />
            </div>

            <div className="bg-[#050505] border border-white/10 p-4 text-xs text-white/50 leading-relaxed">
              After you submit, we&rsquo;ll email you a personal invitation with a secure <span className="text-[#D4AF37]">$300</span> pay link and details on what to expect. Once payment is received, our team will follow up within one business day to schedule your call.
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#D4AF37] text-black py-4 text-xs uppercase tracking-widest hover:bg-[#C9A432] disabled:opacity-50 transition-colors"
              data-testid="strategy-submit"
            >
              {submitting ? 'Sending your invitation…' : 'Request Strategy Session'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
